/**
 * Hybrid AccordionTrigger wrapper.
 * Automatically chooses between the Tailwind and styled-components rendering
 * based on whether styled-system props or a styled() extension are present.
 */

import { useContext } from "react";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { TriggerContainer } from "./styles";
import { AccordionContext } from "./Accordion";
import { AccordionTriggerInner } from "./AccordionTriggerInner";
import { AccordionTriggerTailwind } from "./AccordionTriggerTailwind";
import { type TypeAccordionTriggerProps } from "./AccordionTypes";

export const AccordionTriggerHybrid = (props: TypeAccordionTriggerProps) => {
  const {
    children,
    leftSlot,
    relatedActions,
    overflowMenu,
    rightSlot,
    title,
    headingLevel = "h4",
    ...rest
  } = props;
  const { triggerIcon, triggerPosition, styled } = useContext(AccordionContext);

  if (needsStyledComponents(rest)) {
    // Styled-components branch — mirrors the legacy AccordionTrigger exactly:
    // the styled-system props are destructured out and only `color` plus the
    // remaining triggerProps are forwarded to TriggerContainer.
    const {
      color,
      padding,
      paddingBottom,
      paddingTop,
      paddingX,
      paddingY,
      paddingLeft,
      paddingRight,
      p,
      pb,
      pt,
      pr,
      pl,
      px,
      py,
      fontFamily,
      fontSize,
      fontStyle,
      fontWeight,
      lineHeight,
      textAlign,
      ...triggerProps
    } = rest;

    return (
      <TriggerContainer
        data-styled={styled}
        $styled={styled}
        data-qa-trigger="HERE"
        color={color}
        {...triggerProps}
      >
        <AccordionTriggerInner
          title={title}
          headingLevel={headingLevel}
          leftSlot={leftSlot}
          rightSlot={rightSlot}
          relatedActions={relatedActions}
          overflowMenu={overflowMenu}
          color={color}
          triggerIcon={triggerIcon}
          triggerPosition={triggerPosition}
        />
      </TriggerContainer>
    );
  }

  // Tailwind path (preferred — no styled-components runtime).
  return <AccordionTriggerTailwind {...props} />;
};
